import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Check, Loader2 } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import MFASetup from '../Auth/MFASetup';
import AuthShell, { AuthNotice } from '../Auth/AuthShell';

const Login = () => {
    console.log('[LOGIN] Component rendering - LATEST VERSION 2025-01-07 17:01');
    const mountCount = useRef(0);
    const navigate = useNavigate();
    const { isAuthenticated, user, mfaRequired, mfaSetupRequired, login, clearMFAState } = useContext(AuthContext);
    
    // Test if login function exists
    console.log('[LOGIN] Login function from context:', typeof login);
    console.log('[LOGIN] TESTING BUTTON CLICK HANDLER - this should appear immediately');
    
    // Check if user is already authenticated and redirect to home
    useEffect(() => {
        console.log('[LOGIN] Checking auth state for redirect:', {
            isAuthenticated,
            hasUser: !!user,
            userEmail: user?.email,
            mfaRequired,
            mfaSetupRequired,
            currentPath: window.location.pathname
        });

        if (isAuthenticated && user && !mfaRequired && !mfaSetupRequired) {
            console.log('[LOGIN] User fully authenticated, redirecting to home:', user.email);
            // User is fully authenticated (no MFA required), redirect to home
            navigate('/studies/');
        } else if (isAuthenticated && user && (mfaRequired || mfaSetupRequired)) {
            console.log('[LOGIN] User authenticated but MFA required, staying on login page:', {
                email: user.email,
                mfaRequired,
                mfaSetupRequired
            });
            // User is authenticated but needs MFA - stay on login page to show MFA prompt
            // The MFAPrompt component will handle showing the modal
        } else if (!isAuthenticated) {
            console.log('[LOGIN] User not authenticated, staying on login page');
            // Check local storage as fallback
            const userData = localStorage.getItem('user');
            if (userData) {
                try {
                    const localUser = JSON.parse(userData);
                    console.log('[LOGIN] User found in localStorage but not in context:', localUser.email);
                } catch (e) {
                    console.error('[LOGIN] Error parsing user data from localStorage:', e);
                    localStorage.removeItem('user');
                }
            }
        }
    }, [navigate, isAuthenticated, user, mfaRequired, mfaSetupRequired]);
    
    useEffect(() => {
        mountCount.current += 1;
        console.log('[LOGIN] Component mounted, mount count:', mountCount.current);
        return () => {
            console.log('[LOGIN] Component unmounting');
        };
    }, []);

    const [email, setEmail] = useState(() => {
        const remembered = localStorage.getItem('rememberedEmail');
        console.log('[LOGIN] Initial email state:', remembered || '');
        return remembered || '';
    });
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [networkError, setNetworkError] = useState(null);
    const [pendingApproval, setPendingApproval] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    // AuthContext and navigate are defined above

    // Clear error when inputs change
    useEffect(() => {
        if (error || pendingApproval) {
            console.log('[LOGIN] Clearing error state due to input change');
            setError(null);
            setPendingApproval(false);
        }
    }, [email, password]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('[LOGIN] Form submission started', {
            email: email,
            hasPassword: !!password,
            rememberMe: rememberMe,
            currentLoading: loading
        });

        if (loading) {
            console.log('[LOGIN] Already processing login, skipping');
            return;
        }

        setLoading(true);
        setError(null);
        setNetworkError(null);

        console.log('[LOGIN] Starting login process with credentials:', {
            email: email,
            passwordLength: password?.length || 0,
            rememberMe: rememberMe
        });
        try {
            // Clear remembered email if not checking "Remember Me"
            if (!rememberMe) {
                console.log('[LOGIN] Removing remembered email');
                localStorage.removeItem('rememberedEmail');
            }

            console.log('[LOGIN] About to call login function from AuthContext');
            const result = await login(email, password, rememberMe);
            console.log('[LOGIN] Login function completed, result:', result);
        } catch (error) {
            console.log('[LOGIN] Login error caught:', {
                error: error,
                message: error?.message,
                status: error?.status,
                response: error?.response,
                accountLocked: error?.accountLocked,
                passwordExpired: error?.passwordExpired,
                errorType: typeof error,
                errorKeys: Object.keys(error || {})
            });
            
            // Improved error handling with better safety checks
            if (error && typeof error === 'object') {
                if (error.accountLocked) {
                    console.log('[LOGIN] Setting account locked error');
                    setError(error.message || 'Account is locked'); 
                } else if (error.passwordExpired) {
                    console.log('[LOGIN] Setting password expired error');
                    setError('Your password is expired, please use the forgot password link above to reset your password');
                } else if (error.message === 'Invalid credentials' || error.message === 'Authentication failed') {
                    console.log('[LOGIN] Setting authentication error');
                    setError('Invalid email or password');
                } else if (error.response?.status === 401 || error.status === 401) {
                    console.log('[LOGIN] Setting 401 authentication error');
                    setError('Invalid email or password');
                } else if (error.response?.status === 429 || error.status === 429) {
                    console.log('[LOGIN] Setting rate limit error');
                    setError('Too many login attempts. Please wait a moment and try again.');
                } else if (error.response || error.status) {
                    // The server responded, just with an unexpected status — not a
                    // connectivity problem. Don't mislabel it as one.
                    console.log('[LOGIN] Setting unexpected server error', error.status || error.response?.status);
                    setError('Something went wrong during sign in. Please try again.');
                } else {
                    console.log('[LOGIN] Setting network/connection error');
                    setNetworkError('Unable to connect to the server. Please try again.');
                }
            } else {
                console.log('[LOGIN] Setting generic network error for unknown error type');
                setNetworkError('An unexpected error occurred. Please try again.');
            }
        } finally {
            console.log('[LOGIN] Completing login process');
            setLoading(false);
        }
    };

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        if (name === 'email') {
            setEmail(value);
        } else if (name === 'password') {
            setPassword(value);
        }
    }, []);

    // MFA Setup handlers
    const handleMFASetupComplete = () => {
        console.log('[LOGIN] MFA setup completed successfully');
        // Redirect to home page after successful MFA setup
        navigate('/studies/');
    };

    const handleMFASetupCancel = () => {
        console.log('[LOGIN] MFA setup cancelled');
        // For now, just log - in production you might want to logout
        // or show a warning that MFA is required
    };

    return (
        <>
            <AuthShell title="Welcome back" subtitle="Sign in to your flexCOA account">
                        <form
                            className="space-y-5"
                            onSubmit={(e) => {
                                console.log('[LOGIN] Form onSubmit triggered! Preventing default.');
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('[LOGIN] About to call handleSubmit');

                                // Ensure we don't call handleSubmit if already loading to prevent double submissions
                                if (loading) {
                                    console.log('[LOGIN] Already loading, skipping form submission');
                                    return false;
                                }

                                // Call handleSubmit and catch any unhandled errors
                                try {
                                    handleSubmit(e);
                                } catch (formError) {
                                    console.error('[LOGIN] Error in form submission handler:', formError);
                                    setError('An unexpected error occurred. Please try again.');
                                    setLoading(false);
                                }

                                return false; // Ensure no form submission
                            }}
                        >
                            {/* Email input */}
                            <div>
                                <label htmlFor="email-address" className="label">
                                    Email address
                                </label>
                                <div className="relative">
                                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                    <input
                                        id="email-address"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        className="input h-11 pl-10"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            {/* Password input */}
                            <div>
                                <div className="mb-1.5 flex items-center justify-between">
                                    <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Password
                                    </label>
                                    <Link to="/forgot-password" className="link text-sm">
                                        Forgot password?
                                    </Link>
                                </div>
                                <div className="relative">
                                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                    <input
                                        id="password"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        autoComplete="current-password"
                                        required
                                        className="input h-11 pl-10 pr-11"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={handleInputChange}
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            {/* Remember me (hidden for now) */}
                            <div className="hidden">
                                <label className="flex cursor-pointer items-center">
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                            className="sr-only"
                                        />
                                        <div className={`h-5 w-5 rounded-sm border-2 transition-colors duration-200 ease-in-out ${rememberMe ? 'border-primary-500 bg-primary-500' : 'border-gray-300 dark:border-gray-600'}`}>
                                            <Check className={`h-4 w-4 text-white transition-opacity duration-200 ease-in-out ${rememberMe ? 'opacity-100' : 'opacity-0'}`} />
                                        </div>
                                    </div>
                                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">
                                        Remember me
                                    </span>
                                </label>
                            </div>

                            {/* Error message */}
                            {(error || networkError) && (
                                <AuthNotice tone="danger">{error || networkError}</AuthNotice>
                            )}

                            {/* Pending approval message */}
                            {pendingApproval && (
                                <AuthNotice tone="warning">
                                    Your account is pending approval. Please wait for an admin to approve your role request.
                                </AuthNotice>
                            )}

                            {/* Debug section for MFA issues (development only) */}
                            {(process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') && (mfaRequired || mfaSetupRequired) && (
                                <AuthNotice tone="info" role="status">
                                    <div className="text-center">
                                        <p className="mb-1 font-medium">Debug: MFA State Detected (Dev Mode Only)</p>
                                        <p className="mb-3 text-xs">
                                            MFA Setup Required: {mfaSetupRequired ? 'Yes' : 'No'} | MFA Required: {mfaRequired ? 'Yes' : 'No'}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                console.log('[LOGIN] Clearing stuck MFA state');
                                                clearMFAState();
                                            }}
                                            className="btn btn-secondary btn-sm"
                                        >
                                            Clear MFA State & Logout
                                        </button>
                                    </div>
                                </AuthNotice>
                            )}

                            {/* Submit button */}
                            <button
                                type="submit"
                                className="btn btn-primary btn-lg w-full"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                        Signing in...
                                    </>
                                ) : (
                                    <>
                                        Sign in
                                        <ArrowRight className="h-4 w-4" />
                                    </>
                                )}
                            </button>
                        </form>
            </AuthShell>

            {/* MFA Setup Modal */}
            {mfaSetupRequired && (
                <MFASetup
                    onSetupComplete={handleMFASetupComplete}
                    onCancel={null} // Don't allow cancel when MFA is required
                    userEmail={user?.email}
                    isRequired={true} // MFA is always required when shown on login page
                />
            )}
        </>
    );
}

export default Login;